import CalenderIcon from "@/assets/icons/CalenderWithCircle.svg";
import { CustomButton } from "@/components/ui/Buttons";
import GrowthIcon from "@/assets/icons/GrowTopArrow.svg";
import {
  useGetNextWeekEventsQuery,
  useGetPastWeekEventsQuery,
  useGetThisWeekEventsQuery,
} from "@/redux/feature/calender/calenderApi";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import PreviousYearModal from "./PreviousYearModal";
import EventAllVideosModal from "./EventAllVideosModal";
import ViewAllEvents from "./ViewAllEvents";
import { EventType } from "./EventTypes";
import ProductCard from "../ProductCard";
import { VideoData } from "@/redux/feature/videoDashboard/videoDashboardType";
import SelectModal from "@/pages/ProductStructure/components/SelectModal/SelectModal";
import ProductDetailsModal from "../productDetailsModal/ProductDetailsModal";

type CalenderProps = {
  brandName: string;
  product: string;
  country: string;
  productId: string;
};
const Calender = ({
  brandName,
  product,
  country,
  productId,
}: CalenderProps) => {
  const [isPreviousYearModalOpen, setIsPreviousYearModalOpen] = useState(false);
  const [isViewMoreModalOpen, setIsViewMoreModalOpen] = useState(false);
  const [isViewAllEventsOpen, setIsViewAllEventsOpen] = useState(false);
  const [filter, setFilter] = useState<"past-week" | "this-week" | "next-week">(
    "past-week"
  );
  const [selectedEvent, setSelectedEvent] = useState({
    eventName: "",
    untilDate: 0,
    campaignStatus: "",
    date: "",
    data: [] as VideoData[],
  });

  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoData | null>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

  // Fetch data dynamically based on filter
  const { data: pastWeekEvent } = useGetPastWeekEventsQuery({
    productId: productId,
  });
  const { data: thisWeekEvent } = useGetThisWeekEventsQuery({
    productId: productId,
  });
  const { data: nextWeekEvent } = useGetNextWeekEventsQuery({
    productId: productId,
  });

  const getEvents = (): EventType[] => {
    if (filter === "past-week") return pastWeekEvent?.events || [];
    if (filter === "this-week") return thisWeekEvent?.events || [];
    if (filter === "next-week") return nextWeekEvent?.events || [];
    return [];
  };

  const handlerOpenEventVideos = (
    eventName: string,
    campaignStatus: string,
    untilDate: number,
    date: string,
    data: VideoData[]
  ) => {
    setIsViewMoreModalOpen(true);
    setSelectedEvent({
      eventName,
      campaignStatus,
      untilDate,
      date,
      data: data,
    });
  };

  const events = getEvents();

  return (
    <section id="calendar">
      <div className="flex justify-between items-center">
        <div className="flex gap-x-4 mb-8 items-center">
          <CalenderIcon />
          <div>
            <h5 className="font-grotesk font-medium text-xl leading-6 tracking-[0%]">
              Calendar
            </h5>
            <p className="text-[#6B6B6B] font-grotesk text-xs leading-[17px] mt-2">
              Manage your campaigns and upcoming events
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsViewAllEventsOpen(true)}
          className="text-[#344054] text-xs font-medium border border-[#E4E7EC] px-[5px] py-[3.5px] rounded-lg"
        >
          View All Events
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-x-3 mb-4">
        {["past-week", "this-week", "next-week"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f as any)}
            className={`border px-4 py-2 rounded-lg ${
              filter === f
                ? "bg-[#E4E7EC] text-[#777777] border-[#E4E7EC]"
                : "bg-[#FFFFFF] text-[#000000] border-[#E4E7EC]"
            }`}
          >
            {f.replace("-", " ")}
          </button>
        ))}
      </div>

      {/* Events List */}
      {events?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-8 border border-[#E4E7EC] rounded-lg">
          <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Events Found
          </h3>
          <p className="text-gray-500 text-center max-w-md">
            No events found for this period. Check back later or try selecting a
            different time range.
          </p>
        </div>
      ) : (
        events.map((event) => {
          console.log(event);
          return (
            <div
              key={event?.event + event?.date}
              className="mb-6 border border-[#E4E7EC] px-6 py-4 rounded-lg"
            >
              <div className="flex flex-col md:flex-row justify-between items-start mb-3 gap-3">
                <div className="flex gap-x-3">
                  <p className="text-[15px] text-[#777777] bg-[#F3F3F3] px-2 py-1 rounded-lg">
                    {new Date(event?.date).toLocaleDateString("en-US", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                  <p
                    className={`py-1 px-4 rounded-[10px] text-[15px] ${
                      event?.days_until > 0
                        ? "text-[#006DBA] bg-[#EDF7FF]"
                        : "text-[#9B1C1C] bg-[#FEE2E2]"
                    }`}
                  >
                    {event?.days_until > 0
                      ? `in ${event?.days_until} days`
                      : `${Math.abs(event?.days_until)} days ago`}
                  </p>
                </div>

                <CustomButton
                  onClick={() => setIsSelectModalOpen(true)}
                  variant="outline"
                  border="1px solid #FF6600"
                  borderRadius="12px"
                  padding="10px"
                  color="#FF6600"
                  fontSize="16px"
                  fontWeight="500"
                  className="w-full md:w-[216px]"
                >
                  Place Order
                </CustomButton>
              </div>

              <div className="flex flex-col md:flex-row justify-between items-start gap-3">
                <div>
                  <h3 className="text-xl font-medium">{event?.event}</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    {event?.description}
                  </p>
                  <div className="flex items-center gap-x-2 mt-2">
                    <p className="text-[#777777] text-[15px]">
                      Campaign starts
                    </p>
                    <p className="text-[#0B8000] text-[15px] bg-[#E2FFE3] px-[7.5px] py-1 rounded-[10px]">
                      {event?.campaign_hint}
                    </p>
                  </div>
                </div>
                {/* <CustomButton
                onClick={() => setIsPreviousYearModalOpen(true)}
                variant="outline"
                border="1px solid #E4E7EC"
                borderRadius="12px"
                padding="12px 25px"
                color="#344054"
                fontSize="14px"
                fontWeight="500"
                className="flex gap-x-2 items-center"
              >
                <GrowthIcon />
                View Previous Year Outcomes
              </CustomButton> */}
              </div>
              {/* videos */}
              <div>
                {(() => {
                  const allVideos = event?.videos
                    ? [
                        ...(event.videos.top_ads || []),
                        ...(event.videos.competitor_ads || []),
                        ...(event.videos.competitor_reels || []),
                      ]
                    : [];

                  if (allVideos.length === 0) {
                    return (
                      <div className="mt-4 flex flex-col items-center justify-center py-8 border border-dashed border-[#E4E7EC] rounded-lg bg-[#FAFAFA]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 text-gray-400 mb-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8c.707 0 1.354.293 1.854.793l5.293 5.293a2 2 0 010 2.828l-5.293 5.293a2 2 0 01-2.828 0l-5.293-5.293a2 2 0 010-2.828l5.293-5.293A2.615 2.615 0 0112 8z"
                          />
                        </svg>
                        <p className="text-gray-500 text-lg font-medium">
                          No videos found for this event.
                        </p>
                        <p className="text-gray-400 text-sm mt-1 text-center max-w-xs">
                          Videos will appear here once campaigns are added or
                          uploaded.
                        </p>
                        {/* <div className="text-center">
                        <button
                          onClick={() => handlerOpenEventVideos(event.event)}
                          className="text-[#344054] text-sm font-medium border border-[#E4E7EC] rounded-xl px-[27.5px] py-[5px] mt-4"
                        >
                          View More
                        </button>
                      </div>{" "} */}
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                        {allVideos.slice(0, 3).map((video: VideoData) => (
                          <ProductCard
                            key={video.id}
                            video={video}
                            currentlyPlaying={currentlyPlaying}
                            setCurrentlyPlaying={setCurrentlyPlaying}
                            setIsDetailsModalOpen={setIsDetailsModalOpen}
                            setSelectedVideo={setSelectedVideo}
                          />
                        ))}
                      </div>

                      {allVideos.length > 3 && (
                        <div className="text-center">
                          <button
                            onClick={() =>
                              handlerOpenEventVideos(
                                event.event,
                                event?.campaign_hint,
                                event?.days_until,
                                event?.date,
                                allVideos
                              )
                            }
                            className="text-[#344054] text-sm font-medium border border-[#E4E7EC] rounded-xl px-[27.5px] py-[5px] mt-4"
                          >
                            View More
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          );
        })
      )}

      {/* Modals */}
      <Modal
        isOpen={isPreviousYearModalOpen}
        setOpen={setIsPreviousYearModalOpen}
        width="1108px"
        padding="15px 22px"
      >
        <PreviousYearModal
          setIsPreviousYearModalOpen={setIsPreviousYearModalOpen}
        />
      </Modal>

      <Modal
        isOpen={isViewMoreModalOpen}
        setOpen={setIsViewMoreModalOpen}
        width="1108px"
        padding="15px 22px"
      >
        <EventAllVideosModal productId={productId} event={selectedEvent} />
      </Modal>

      <Modal
        isOpen={isViewAllEventsOpen}
        setOpen={setIsViewAllEventsOpen}
        width="1108px"
        padding="15px 22px"
      >
        <ViewAllEvents
          productId={productId}
          setIsSelectModalOpen={setIsSelectModalOpen}
        />
      </Modal>

      <Modal
        padding="12px"
        width="750px"
        isOpen={isDetailsModalOpen}
        setOpen={setIsDetailsModalOpen}
      >
        <ProductDetailsModal
          showGenerateScript={true}
          video={selectedVideo as VideoData}
          setIsDetailsModalOpen={setIsDetailsModalOpen}
          setIsSelectModalOpen={setIsSelectModalOpen}
        />
      </Modal>

      <Modal
        isOpen={isSelectModalOpen}
        setOpen={setIsSelectModalOpen}
        width="min(900px, 95vw)"
      >
        <SelectModal
          isOpen={isSelectModalOpen}
          setOpen={setIsSelectModalOpen}
        />
      </Modal>
    </section>
  );
};

export default Calender;
